import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Link, useNavigate } from "react-router-dom";
import {
  useListPaintings, useListCategories, useListCollections, useGetGallerySummary,
  useDeletePainting, useToggleFeatured, useToggleSale, useHealthCheck,
  useListOrders, useOrderStats, useUpdateOrderStatus, fetchMe, useDeleteCategory, useDeleteCollection,
} from "@/lib/api";
import { useLocale } from "@/context/locale-context";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Pencil, Trash2, Plus, ArrowLeft, Image as ImageIcon,
  Package, DollarSign, Clock, CheckCircle2, XCircle, TrendingUp,
  ShoppingBag, Eye, Truck, BarChart3, Settings, LogOut,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { PaintingFormDialog } from "@/components/admin/painting-form";
import { CategoryFormDialog, CollectionFormDialog } from "@/components/admin/category-form";
import { SettingsDialog } from "@/components/admin/settings-dialog";

const STATUS_MAP = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  approved: { label: "Approved", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
  shipped: { label: "Shipped", color: "bg-blue-100 text-blue-800 border-blue-200", icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Package },
};

function OrderStatusBadge({ status }) {
  const config = STATUS_MAP[status] || STATUS_MAP.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-medium uppercase tracking-widest border ${config.color}`}>
      <Icon className="w-3 h-3" />{config.label}
    </span>
  );
}

function TaxonomyCard({ item, type, paintings, onEdit, onDelete }) {
  const isCollection = type === "collection";
  const assignedPaintings = paintings?.filter((painting) =>
    isCollection ? painting.collectionId === item.id : painting.categoryId === item.id
  ) || [];
  const label = isCollection ? "collection" : "category";
  const galleryFilter = isCollection ? "collectionId" : "categoryId";

  return (
    <div className="border bg-card overflow-hidden" data-testid={`${type}-card-${item.id}`}>
      {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="w-full aspect-[16/9] object-cover" />}
      <div className="p-6">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-serif text-xl">{item.name}</h3>
          <div className="flex shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} aria-label={`Edit ${label} ${item.name}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10" aria-label={`Delete ${label} ${item.name}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif">Delete {label}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete <strong>"{item.name}"</strong> and remove it from assigned paintings.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                  <AlertDialogAction className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={onDelete}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2">{item.description || "No description"}</p>
      </div>
      <Separator />
      <div className="px-6 py-4 bg-muted/20 flex items-center justify-between">
        <div className="flex gap-6 text-xs text-muted-foreground">
          <span><strong className="text-foreground">{assignedPaintings.length}</strong> pieces</span>
          <span><strong className="text-foreground">{assignedPaintings.filter((painting) => !painting.sold).length}</strong> available</span>
        </div>
        <Link to={`/gallery?${galleryFilter}=${item.id}`} target="_blank" className="text-[10px] uppercase tracking-widest text-accent hover:underline">View</Link>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t, formatPrice } = useLocale();
  const [adminUser, setAdminUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_user")); } catch { return null; }
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin/login"); return; }
    fetchMe().then((user) => { setAdminUser(user); setAuthChecked(true); })
      .catch(() => { localStorage.removeItem("admin_token"); localStorage.removeItem("admin_user"); navigate("/admin/login"); });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  const { data: summary } = useGetGallerySummary();
  const { data: paintings, isLoading: isLoadingPaintings } = useListPaintings();
  const { data: categories, isLoading: isLoadingCategories } = useListCategories();
  const { data: collections, isLoading: isLoadingCollections } = useListCollections();
  const { data: orders, isLoading: isLoadingOrders } = useListOrders();
  const { data: orderStats } = useOrderStats();
  const { data: health } = useHealthCheck();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const deletePainting = useDeletePainting();
  const deleteCategory = useDeleteCategory();
  const deleteCollection = useDeleteCollection();
  const toggleFeatured = useToggleFeatured();
  const toggleSale = useToggleSale();
  const updateOrderStatus = useUpdateOrderStatus();
  const [editingPainting, setEditingPainting] = useState(null);
  const [isPaintingFormOpen, setIsPaintingFormOpen] = useState(false);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [isCollectionFormOpen, setIsCollectionFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");

  const invalidateQueries = () => { queryClient.invalidateQueries(); };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this piece?")) {
      try { await deletePainting.mutateAsync(id); toast({ title: "Painting deleted." }); invalidateQueries(); }
      catch { toast({ title: "Failed to delete.", variant: "destructive" }); }
    }
  };

  const handleToggleFeatured = async (id) => {
    try { await toggleFeatured.mutateAsync(id); invalidateQueries(); }
    catch { toast({ title: "Failed to update.", variant: "destructive" }); }
  };

  const handleToggleSale = async (id) => {
    try { await toggleSale.mutateAsync(id); invalidateQueries(); }
    catch { toast({ title: "Failed to update.", variant: "destructive" }); }
  };

  const handleOrderStatus = async (orderId, newStatus) => {
    try {
      await updateOrderStatus.mutateAsync({ id: orderId, status: newStatus });
      toast({ title: `Order #${orderId} ${newStatus}.` });
      invalidateQueries();
    } catch { toast({ title: "Failed to update order.", variant: "destructive" }); }
  };

  const filteredOrders = orders?.filter(o => orderFilter === "all" ? true : o.status === orderFilter) || [];

  if (!authChecked) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-center"><div className="font-serif text-2xl mb-2">Taller Del Profe.</div><p className="text-xs uppercase tracking-widest text-muted-foreground animate-pulse">Loading dashboard...</p></div></div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      {/* Top Bar */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors uppercase tracking-widest mb-2" data-testid="back-to-site">
                <ArrowLeft className="w-3.5 h-3.5" /> {t.admin.backToSite}
              </Link>
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-serif">{t.admin.title}</h1>
                {health && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-green-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    {t.admin.systemOk}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button onClick={() => setIsSettingsOpen(true)} variant="outline" size="sm" className="rounded-none uppercase tracking-widest text-[10px] h-9" data-testid="settings-btn">
                <Settings className="w-3.5 h-3.5 mr-1.5" /> {adminUser?.username || "Settings"}
              </Button>
              <Button onClick={() => { setEditingPainting(null); setIsPaintingFormOpen(true); }} size="sm" className="rounded-none uppercase tracking-widest text-[10px] h-9" data-testid="new-piece-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> {t.admin.newPiece}
              </Button>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="rounded-none uppercase tracking-widest text-[10px] h-9 text-muted-foreground" data-testid="logout-btn">
                <LogOut className="w-3.5 h-3.5 mr-1.5" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4 mb-8 md:mb-10">
          <div className="bg-card border p-5 space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground"><Package className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">{t.admin.totalInventory}</span></div>
            <div className="text-2xl font-serif" data-testid="total-inventory">{summary?.totalPaintings || 0}</div>
          </div>
          <div className="bg-card border p-5 space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground"><DollarSign className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">Revenue</span></div>
            <div className="text-2xl font-serif text-green-700" data-testid="total-revenue">{formatPrice(orderStats?.totalRevenue || 0)}</div>
          </div>
          <div className="bg-card border p-5 space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground"><ShoppingBag className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">Orders</span></div>
            <div className="text-2xl font-serif">{orderStats?.totalOrders || 0}</div>
          </div>
          <div className="bg-card border p-5 space-y-1">
            <div className="flex items-center gap-2 text-amber-600"><Clock className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">Pending</span></div>
            <div className="text-2xl font-serif text-amber-700">{orderStats?.pendingOrders || 0}</div>
            {orderStats?.pendingRevenue > 0 && <div className="text-xs text-muted-foreground">{formatPrice(orderStats.pendingRevenue)}</div>}
          </div>
          <div className="bg-card border p-5 space-y-1">
            <div className="flex items-center gap-2 text-accent"><TrendingUp className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">{t.admin.onSale}</span></div>
            <div className="text-2xl font-serif">{summary?.onSaleCount || 0}</div>
          </div>
          <div className="bg-card border p-5 space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground"><BarChart3 className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">{t.admin.sold}</span></div>
            <div className="text-2xl font-serif text-accent">{summary?.soldCount || 0}</div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="rounded-none w-full justify-start border-b bg-transparent p-0 mb-8 flex-wrap">
            <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none uppercase tracking-widest text-xs px-6 py-3" data-testid="orders-tab">
              Orders {orderStats?.pendingOrders > 0 && <span className="ml-2 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] inline-flex items-center justify-center">{orderStats.pendingOrders}</span>}
            </TabsTrigger>
            <TabsTrigger value="inventory" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none uppercase tracking-widest text-xs px-6 py-3" data-testid="inventory-tab">{t.admin.inventory}</TabsTrigger>
            <TabsTrigger value="categories" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none uppercase tracking-widest text-xs px-6 py-3" data-testid="categories-tab">Categories</TabsTrigger>
            <TabsTrigger value="collections" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none uppercase tracking-widest text-xs px-6 py-3" data-testid="collections-tab">{t.admin.collections}</TabsTrigger>
          </TabsList>

          {/* ORDERS TAB */}
          <TabsContent value="orders" className="mt-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif">Sales & Orders</h2>
              <Select value={orderFilter} onValueChange={setOrderFilter}>
                <SelectTrigger className="w-[160px] rounded-none text-xs h-9" data-testid="order-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border bg-card overflow-x-auto">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase tracking-widest text-[10px] w-20">Order</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">Customer</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">Items</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">Total</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">Status</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">Date</TableHead>
                    <TableHead className="text-right uppercase tracking-widest text-[10px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingOrders ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12">Loading orders...</TableCell></TableRow>
                  ) : filteredOrders.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-16 text-muted-foreground">
                      <ShoppingBag className="w-8 h-8 mx-auto mb-3 opacity-30" />
                      <p className="font-serif text-lg mb-1">No orders yet</p>
                      <p className="text-xs">Orders will appear here when customers make purchases.</p>
                    </TableCell></TableRow>
                  ) : filteredOrders.map((order) => (
                    <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                      <TableCell><span className="font-mono text-xs font-medium">#{order.id}</span></TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{order.firstName} {order.lastName}</div>
                        <div className="text-xs text-muted-foreground">{order.email}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">{order.city}, {order.country}</div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {order.items?.slice(0, 2).map((item, i) => (
                            <div key={i} className="text-xs truncate max-w-[180px]">{item.title || `Item ${i + 1}`}</div>
                          ))}
                          {order.items?.length > 2 && <div className="text-[10px] text-muted-foreground">+{order.items.length - 2} more</div>}
                        </div>
                      </TableCell>
                      <TableCell><span className="font-serif font-medium">{formatPrice(order.totalUSD)}</span></TableCell>
                      <TableCell><OrderStatusBadge status={order.status} /></TableCell>
                      <TableCell><span className="text-xs text-muted-foreground">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {order.status === "pending" && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50 hover:text-green-700" onClick={() => handleOrderStatus(order.id, "approved")} title="Approve" data-testid={`approve-order-${order.id}`}>
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleOrderStatus(order.id, "rejected")} title="Reject" data-testid={`reject-order-${order.id}`}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {order.status === "approved" && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => handleOrderStatus(order.id, "shipped")} title="Mark Shipped" data-testid={`ship-order-${order.id}`}>
                              <Truck className="w-4 h-4" />
                            </Button>
                          )}
                          {order.status === "shipped" && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:bg-emerald-50" onClick={() => handleOrderStatus(order.id, "delivered")} title="Mark Delivered" data-testid={`deliver-order-${order.id}`}>
                              <Package className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* INVENTORY TAB */}
          <TabsContent value="inventory" className="mt-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif">{t.admin.inventory}</h2>
              <span className="text-xs text-muted-foreground uppercase tracking-widest">{paintings?.length || 0} pieces</span>
            </div>
            <div className="border bg-card overflow-x-auto">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16"></TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">{t.admin.titleDetails}</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">Price</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">{t.admin.status}</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">{t.admin.featured}</TableHead>
                    <TableHead className="uppercase tracking-widest text-[10px]">{t.admin.onSale}</TableHead>
                    <TableHead className="text-right uppercase tracking-widest text-[10px]">{t.admin.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingPaintings ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
                  ) : paintings?.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-16 text-muted-foreground">No inventory available. Add your first piece.</TableCell></TableRow>
                  ) : paintings?.map((painting) => (
                    <TableRow key={painting.id} className={painting.sold ? "opacity-50 bg-muted/20" : ""} data-testid={`painting-row-${painting.id}`}>
                      <TableCell>
                        <div className="w-12 h-14 bg-muted flex items-center justify-center border overflow-hidden">
                          {painting.imageUrl ? <img src={painting.imageUrl} className="w-full h-full object-cover" alt="" /> : <ImageIcon className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-serif font-medium">{painting.title}</div>
                        <div className="text-[10px] text-muted-foreground flex gap-2 items-center mt-0.5">
                          {painting.categoryName && <span>Category: {painting.categoryName}</span>}
                          {painting.collectionName && <span>• Collection: {painting.collectionName}</span>}
                          {painting.technique && <span>• {painting.technique}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{formatPrice(painting.price)}</div>
                        {painting.onSale && painting.salePrice && <div className="text-[10px] text-accent font-medium">{formatPrice(painting.salePrice)}</div>}
                      </TableCell>
                      <TableCell>
                        {painting.sold ? (
                          <Badge variant="secondary" className="rounded-none uppercase text-[10px] tracking-wider">{t.card.sold}</Badge>
                        ) : (
                          <Badge variant="outline" className="rounded-none uppercase text-[10px] tracking-wider border-green-500/30 text-green-700">{t.admin.available}</Badge>
                        )}
                      </TableCell>
                      <TableCell><Switch checked={painting.featured} onCheckedChange={() => handleToggleFeatured(painting.id)} disabled={painting.sold} /></TableCell>
                      <TableCell><Switch checked={painting.onSale} onCheckedChange={() => handleToggleSale(painting.id)} disabled={painting.sold} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Link to={`/painting/${painting.id}`} target="_blank">
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="View"><Eye className="w-3.5 h-3.5" /></Button>
                          </Link>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingPainting(painting); setIsPaintingFormOpen(true); }} data-testid={`edit-painting-${painting.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => handleDelete(painting.id)} data-testid={`delete-painting-${painting.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CATEGORIES TAB */}
          <TabsContent value="categories" className="mt-0">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-serif">Categories</h2>
                  <p className="text-sm text-muted-foreground">Organize paintings by category and choose their home images.</p>
                </div>
                <Button onClick={() => { setEditingCategory(null); setIsCategoryFormOpen(true); }} variant="outline" size="sm" className="rounded-none uppercase tracking-widest text-[10px] h-9">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> New Category
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoadingCategories ? Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-none" />) :
                  categories?.length ? categories.map((category) => (
                    <TaxonomyCard
                      key={category.id}
                      item={category}
                      type="category"
                      paintings={paintings}
                      onEdit={() => { setEditingCategory(category); setIsCategoryFormOpen(true); }}
                      onDelete={async () => {
                        try {
                          await deleteCategory.mutateAsync(category.id);
                          toast({ title: "Category deleted." });
                        } catch {
                          toast({ title: "Failed to delete category.", variant: "destructive" });
                        }
                      }}
                    />
                  )) : <div className="col-span-full py-12 text-center text-muted-foreground border bg-card">No categories yet.</div>}
              </div>
          </TabsContent>

          {/* COLLECTIONS TAB */}
          <TabsContent value="collections" className="mt-0">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-serif">Collections</h2>
                  <p className="text-sm text-muted-foreground">Create collections and choose the images shown on the home page.</p>
                </div>
                <Button onClick={() => { setEditingCollection(null); setIsCollectionFormOpen(true); }} variant="outline" size="sm" className="rounded-none uppercase tracking-widest text-[10px] h-9">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> New Collection
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoadingCollections ? Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-none" />) :
                  collections?.length ? collections.map((collection) => (
                    <TaxonomyCard
                      key={collection.id}
                      item={collection}
                      type="collection"
                      paintings={paintings}
                      onEdit={() => { setEditingCollection(collection); setIsCollectionFormOpen(true); }}
                      onDelete={async () => {
                        try {
                          await deleteCollection.mutateAsync(collection.id);
                          toast({ title: "Collection deleted." });
                        } catch {
                          toast({ title: "Failed to delete collection.", variant: "destructive" });
                        }
                      }}
                    />
                  )) : <div className="col-span-full py-12 text-center text-muted-foreground border bg-card">No collections yet.</div>}
              </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      {isPaintingFormOpen && <PaintingFormDialog isOpen={isPaintingFormOpen} onOpenChange={setIsPaintingFormOpen} painting={editingPainting} categories={categories || []} collections={collections || []} onSuccess={() => { setIsPaintingFormOpen(false); invalidateQueries(); }} />}
      {isCategoryFormOpen && <CategoryFormDialog isOpen={isCategoryFormOpen} onOpenChange={setIsCategoryFormOpen} item={editingCategory} onSuccess={() => { setIsCategoryFormOpen(false); invalidateQueries(); }} />}
      {isCollectionFormOpen && <CollectionFormDialog isOpen={isCollectionFormOpen} onOpenChange={setIsCollectionFormOpen} item={editingCollection} onSuccess={() => { setIsCollectionFormOpen(false); invalidateQueries(); }} />}
      {isSettingsOpen && <SettingsDialog isOpen={isSettingsOpen} onOpenChange={setIsSettingsOpen} currentUser={adminUser} onUserUpdated={setAdminUser} />}
    </div>
  );
}
